import React, { useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, CalendarDays, MessageSquare, Shield, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTheme } from '../contexts/ThemeContext';
interface FeatureCarouselScreenProps {
  onContinue: () => void;
}
export function FeatureCarouselScreen({
  onContinue
}: FeatureCarouselScreenProps) {
  const {
    isLight
  } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const features = [{
    id: 'calls',
    icon: Phone,
    title: 'AI answers your calls',
    description: 'Never miss an opportunity. Vexu handles your missed calls with a natural-sounding AI voice.',
    color: 'text-blue-500',
    bg: isLight ? 'bg-blue-50' : 'bg-blue-500/10',
    border: isLight ? 'border-blue-100' : 'border-blue-500/20'
  }, {
    id: 'calendar',
    icon: CalendarDays,
    title: 'Books your meetings',
    description: 'Vexu checks your availability and schedules appointments directly onto your calendar.',
    color: 'text-purple-500',
    bg: isLight ? 'bg-purple-50' : 'bg-purple-500/10',
    border: isLight ? 'border-purple-100' : 'border-purple-500/20'
  }, {
    id: 'summaries',
    icon: Shield,
    title: 'Instant summaries',
    description: 'Get concise transcripts and actionable summaries immediately after every interaction.',
    color: 'text-orange-500',
    bg: isLight ? 'bg-orange-50' : 'bg-orange-500/10',
    border: isLight ? 'border-orange-100' : 'border-orange-500/20'
  }];
  const handleNext = () => {
    if (currentIndex < features.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      onContinue();
    }
  };
  return <div className="h-full flex flex-col p-6 relative overflow-hidden" data-id="element-1188">
      {/* Background orbs */}
      <div className={`absolute top-20 -right-20 w-60 h-60 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/15' : 'bg-vexu/10'}`} data-id="element-1189" />
      <div className={`absolute bottom-40 -left-20 w-60 h-60 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-blue-500/10' : 'bg-blue-500/5'}`} data-id="element-1190" />

      {/* Progress dots */}
      <div className="flex justify-center gap-2 pt-8 relative z-10" data-id="element-1191">
        {features.map((_, i) => <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === currentIndex ? `w-6 ${isLight ? 'bg-vexu' : 'bg-vexu'}` : `w-1.5 ${isLight ? 'bg-slate-200' : 'bg-white/20'}`}`} data-id="element-1192" />)}
      </div>

      <div className="flex-1 flex flex-col items-center justify-center relative z-10 w-full" data-id="element-1193">
        <AnimatePresence mode="wait" data-id="element-1194">
          <motion.div key={currentIndex} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.3
        }} className="w-full flex flex-col items-center text-center" data-id="element-1195">
            {/* Feature Icon Card */}
            <div className={`w-32 h-32 rounded-3xl flex items-center justify-center mb-8 border backdrop-blur-xl shadow-xl ${features[currentIndex].bg} ${features[currentIndex].border}`} data-id="element-1196">
              {createElement(features[currentIndex].icon, {
              className: `w-14 h-14 ${features[currentIndex].color}`,
              strokeWidth: 1.5
            })}
            </div>

            <h2 className={`text-3xl font-bold mb-4 tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1197">
              {features[currentIndex].title}
            </h2>

            <p className={`text-lg max-w-[280px] leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1198">
              {features[currentIndex].description}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="w-full pb-8 relative z-10" data-id="element-1199">
        <Button fullWidth size="lg" onClick={handleNext} icon={currentIndex === features.length - 1 ? undefined : <ArrowRight className="w-5 h-5" data-id="element-1201" />} className="flex-row-reverse" data-id="element-1200">
          {currentIndex === features.length - 1 ? 'Continue' : 'Next'}
        </Button>
        <button onClick={onContinue} className={`w-full text-center text-sm font-medium mt-4 transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-1202">
          Skip
        </button>
      </div>
    </div>;
}